import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/use-toast';
import { linkedScheduledJobKey, useLinkedScheduledJob } from '../hooks/use-linked-scheduled-job';
import { useScheduledJobActions } from '../hooks/use-scheduled-job-actions';
import type { ScheduledJobItem } from '../services/scheduled-jobs.types';
import { EditScheduledJobModal } from './edit-scheduled-job-modal';

/** Opens the routine from `/scheduler?job=<id>` (e.g. the routine finish email link). */
export function LinkedScheduledJobDialog() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { jobId, job, status, clear } = useLinkedScheduledJob();
  const { deleteJob, openChat } = useScheduledJobActions();
  // The notice belongs to the first answer for a link only. A later refetch
  // (e.g. after deleting the routine) must not report "can't be opened".
  const settledJobId = useRef<string | null>(null);

  useEffect(() => {
    // A cleared link starts over, so opening the same link again notifies again.
    if (!jobId) {
      settledJobId.current = null;
      return;
    }
    if (settledJobId.current === jobId) return;
    if (status === 'loading' || status === 'idle') return;
    settledJobId.current = jobId;
    if (status === 'not_found') {
      toast({
        title: t('SCHEDULED_JOB_LINK_NOT_FOUND', {
          defaultValue: "This routine can't be opened. It may have been deleted.",
        }),
      });
      clear();
    }
    if (status === 'error') {
      toast({
        title: t('FAILED_TO_LOAD_SCHEDULED_JOBS', {
          defaultValue: 'Failed to load scheduled jobs.',
        }),
      });
      clear();
    }
  }, [jobId, status, toast, t, clear]);

  return (
    <EditScheduledJobModal
      open={status === 'found' && !!job}
      job={job}
      onOpenChange={(open) => {
        if (!open) clear();
      }}
      onJobSaved={(saved) =>
        queryClient.setQueryData<ScheduledJobItem | null>(linkedScheduledJobKey(jobId), saved)
      }
      onOpenChat={(target) => {
        clear();
        openChat(target);
      }}
      onDelete={deleteJob}
    />
  );
}
